import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { 
  DollarSign,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Receipt
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

export const AdditionalExpensesSection = ({ onTotalChange, className }) => {
  const [expenses, setExpenses] = useState([]);
  const [totalMonthly, setTotalMonthly] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    monthly_cost: '',
    category: 'Other',
    notes: ''
  });

  const fetchExpenses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/expenses`);
      const data = await response.json();
      setExpenses(data.expenses || []);
      setTotalMonthly(data.total_monthly || 0);
      if (onTotalChange) {
        onTotalChange(data.total_monthly || 0);
      }
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const openAddDialog = () => {
    setEditingExpense(null);
    setFormData({
      name: '',
      monthly_cost: '',
      category: 'Other',
      notes: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setFormData({
      name: expense.name,
      monthly_cost: expense.monthly_cost.toString(),
      category: expense.category || 'Other',
      notes: expense.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a service name');
      return;
    }
    if (!formData.monthly_cost || parseFloat(formData.monthly_cost) <= 0) {
      toast.error('Please enter a valid monthly cost');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        monthly_cost: parseFloat(formData.monthly_cost),
        category: formData.category || 'Other',
        notes: formData.notes || ''
      };

      if (editingExpense) {
        // Update existing
        const response = await fetch(`${API_URL}/api/expenses/${editingExpense.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          toast.success('Expense updated');
        } else {
          throw new Error('Failed to update');
        }
      } else {
        // Add new
        const response = await fetch(`${API_URL}/api/expenses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          toast.success('Expense added');
        } else {
          throw new Error('Failed to add');
        }
      }

      setDialogOpen(false);
      fetchExpenses();
    } catch (error) {
      console.error('Failed to save expense:', error);
      toast.error('Failed to save expense');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!confirm('Delete this expense?')) return;

    try {
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Expense deleted');
        fetchExpenses();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Failed to delete expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const categories = ['Software', 'Hardware', 'Services', 'Subscriptions', 'Maintenance', 'Other'];

  return (
    <div className={cn('space-y-4', className)} data-testid="additional-expenses-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Receipt className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Additional IT Expenses
            </h4>
            <p className="text-xs text-muted-foreground">
              Track other monthly costs not captured by integrations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchExpenses} disabled={loading}>
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </Button>
          <Button size="sm" onClick={openAddDialog} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Total Card */}
      <Card className="p-4 bg-primary/5 border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">Total Monthly</span>
          </div>
          <span className="text-2xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {formatCurrency(totalMonthly)}
          </span>
        </div>
      </Card>

      {/* Expenses Table */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : expenses.length === 0 ? (
        <Card className="p-8 text-center">
          <Receipt className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No additional expenses added yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Add Expense" to track other IT costs
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service / Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Monthly Cost</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{expense.name}</p>
                      {expense.notes && (
                        <p className="text-xs text-muted-foreground truncate max-w-[300px]">
                          {expense.notes}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {expense.category || 'Other'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono font-medium">
                    {formatCurrency(expense.monthly_cost)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(expense)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(expense.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {editingExpense ? 'Edit Expense' : 'Add Expense'}
            </DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update the expense details' : 'Add a new monthly IT expense'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="expense-name">Service / Description *</Label>
              <Input
                id="expense-name"
                placeholder="e.g., Adobe Creative Cloud"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-2"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="expense-cost">Monthly Cost *</Label>
                <div className="relative mt-2">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="expense-cost"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.monthly_cost}
                    onChange={(e) => setFormData({ ...formData, monthly_cost: e.target.value })}
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="expense-category">Category</Label>
                <select
                  id="expense-category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="mt-2 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="expense-notes">Notes (optional)</Label>
              <Input
                id="expense-notes"
                placeholder="Any additional details..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingExpense ? 'Update' : 'Add Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
